import React, { useState, useEffect, useMemo } from "react";
import { checkQuantity, firstQuantityError } from "../utils/quantityCheck";
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Stack,
} from "@mui/material";
import { Delete as DeleteIcon, Add as AddIcon } from "@mui/icons-material";
import axios from "axios";
import { useTranslation } from "react-i18next";

// Ledger rows with no batch code come back as "". MUI Select treats "" as "nothing chosen",
// so it gets its own value on screen and is sent back to the server as "".
const NO_BATCH = "__NO_BATCH__";
const toUiBatch = (batchNo) => (batchNo == null || batchNo === "" ? NO_BATCH : batchNo);
const toServerBatch = (batch) => (batch === NO_BATCH ? "" : batch);

const emptyRow = () => ({
  id: Date.now() + Math.random(), selectedItem: null, batches: [], selectedBatch: "",
  systemQty: 0, actualQty: "", difference: 0, loadingBatch: false,
});

/**
 * Item picker that searches the server as the user types (the same /items/search that Wastage
 * Entry and Stock Conversion use).
 *
 * It used to read the shared "pos-item-cache-v1" localStorage key. The web POS and KOT screens
 * write that key with their own item shape ({ id, name, ... }), so after either was opened this
 * screen found a "cache" with no itemName/itemId: every option rendered blank and nothing could
 * be searched or loaded, and because the cache existed it was never refetched.
 */
const ItemSearchField = ({ value, onChange, tenancyId, token, t }) => {
  const [query, setQuery] = useState("");
  const [options, setOptions] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const timer = setTimeout(async () => {
      setBusy(true);
      setError("");
      try {
        const res = await axios.get(`/api/${tenancyId}/items/search`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { q: query.trim(), page: 0, size: 25 },
        });
        setOptions(res.data?.content || []);
      } catch (err) {
        setOptions([]);
        setError(t("Could not search items."));
      } finally {
        setBusy(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [query, tenancyId, token, t]);

  return (
    <Autocomplete
      options={value && !options.some((o) => o.itemId === value.itemId) ? [value, ...options] : options}
      getOptionLabel={(o) => o.itemName || ""}
      isOptionEqualToValue={(o, v) => o.itemId === v.itemId}
      filterOptions={(x) => x}
      loading={busy}
      value={value}
      onChange={(e, v) => onChange(v)}
      onInputChange={(e, v, reason) => { if (reason === "input" || reason === "clear") setQuery(v); }}
      noOptionsText={error || t("No items found")}
      renderInput={(params) => (
        <TextField {...params} size="small" placeholder={t("Search item")} error={Boolean(error)} />
      )}
    />
  );
};

const PhysicalStockCorrection = () => {
  const { t } = useTranslation();
  const [branch, setBranch] = useState("");
  const [branches, setBranches] = useState([]);
  const [rows, setRows] = useState([emptyRow()]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: "", severity: "info" });

  const tenancyId = localStorage.getItem("tenancyId");
  const token = localStorage.getItem("jwtToken");

  const allowedBranches = useMemo(() => {
    try {
      const raw = localStorage.getItem("allowedBranches");
      const list = raw ? JSON.parse(raw) : [];
      return Array.isArray(list) ? list : [];
    } catch {
      return [];
    }
  }, []);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const response = await fetch(`/api/${tenancyId}/branches`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await response.json();
        const list = Array.isArray(data) ? data : data.branches || data.data || [];
        const filtered = allowedBranches.length
          ? list.filter((b) => allowedBranches.includes(b.branchCode))
          : list;
        setBranches(filtered);
      } catch (err) {
        console.error("Error fetching initial data:", err);
      }
    };
    fetchInitialData();
  }, [tenancyId, token, allowedBranches]);

  const handleItemChange = (id, newItem) => {
    setRows(prev => prev.map(row =>
      row.id === id
        ? { ...row, selectedItem: newItem, selectedBatch: "", systemQty: 0, actualQty: "", difference: 0, batches: [], loadingBatch: false }
        : row
    ));
  };

  const handleLoadBatches = async (id) => {
    const row = rows.find(r => r.id === id);
    const itemId = row?.selectedItem?.itemId;
    if (!branch || !itemId) return;

    setRows(prev => prev.map(r => r.id === id ? { ...r, loadingBatch: true, batches: [], selectedBatch: "" } : r));
    try {
      const res = await axios.get(`/api/${tenancyId}/inventory/item-batches`, {
        headers: { Authorization: `Bearer ${token}` },
        // includeNegative: a batch that has gone below zero is still part of the item's
        // total in Item Movement. Leaving it off this list meant correcting the other
        // batches could never bring the item to the counted qty.
        params: { branchCode: branch, itemId, includeNegative: true },
      });
      const fetched = (res.data || []).map(b => ({
        batchNo: toUiBatch(b.batchNo),
        currentStock: Number(b.currentStock || 0),
      }));
      if (fetched.length > 0) {
        // One batch is the usual case; pick it so the count can be typed straight away.
        const only = fetched.length === 1 ? fetched[0] : null;
        setRows(prev => prev.map(r =>
          r.id === id
            ? {
                ...r,
                batches: fetched,
                loadingBatch: false,
                selectedBatch: only ? only.batchNo : "",
                systemQty: only ? only.currentStock : 0,
                difference: only && r.actualQty !== "" ? Number(r.actualQty) - only.currentStock : 0,
              }
            : r
        ));
      } else {
        // No existing stock — inject a synthetic entry so the user can still
        // enter the physical count for an item with zero system quantity.
        setRows(prev => prev.map(r =>
          r.id === id
            ? {
                ...r,
                batches: [{ batchNo: "NO STOCK", currentStock: 0 }],
                selectedBatch: "NO STOCK",
                systemQty: 0,
                difference: 0,
                loadingBatch: false,
              }
            : r
        ));
      }
    } catch (err) {
      console.error("Error fetching batches:", err);
      setRows(prev => prev.map(r => r.id === id ? { ...r, loadingBatch: false } : r));
    }
  };

  const handleBatchChange = (id, batchNo) => {
    setRows(prev => prev.map(row => {
      if (row.id === id) {
        const batchData = row.batches.find(b => b.batchNo === batchNo);
        const systemQty = batchData ? Number(batchData.currentStock || 0) : 0;
        const diff = row.actualQty !== "" ? Number(row.actualQty) - systemQty : 0;
        return { ...row, selectedBatch: batchNo, systemQty, difference: diff };
      }
      return row;
    }));
  };

  const handleActualQtyChange = (id, value) => {
    setRows(prev => prev.map(row => {
      if (row.id === id) {
        const diff = value !== "" ? Number(value) - row.systemQty : 0;
        return { ...row, actualQty: value, difference: diff };
      }
      return row;
    }));
  };

  const handleAddRow = () => {
    setRows([...rows, emptyRow()]);
  };

  const handleRemoveRow = (id) => {
    if (rows.length > 1) setRows(rows.filter(row => row.id !== id));
  };

  const handleSave = async () => {
    const validRows = rows.filter(r => branch && r.selectedItem && r.selectedBatch && r.actualQty !== "");
    if (validRows.length === 0) {
      setMessage({ text: t("Please fill at least one row completely"), severity: "warning" });
      return;
    }

    // Stop a scanned barcode reaching the database. V047's CHECK constraint catches it too,
    // but only after the whole form is filled in and only as a raw constraint violation -
    // which is how seven unrecoverable rows got into physical_stock_mst in the first place.
    const bad = firstQuantityError(validRows, (r) => r.actualQty);
    if (bad) {
      setMessage({ text: `Row ${bad.index + 1}: ${bad.error}`, severity: "error" });
      return;
    }

    // Two rows on the same item and batch would each be applied to the same stock, so the
    // batch would end at neither count.
    const seen = new Set();
    for (const r of validRows) {
      const k = `${r.selectedItem.itemId}|${r.selectedBatch}`;
      if (seen.has(k)) {
        setMessage({ text: `${r.selectedItem.itemName}: ${t("the same batch is entered twice")}`, severity: "error" });
        return;
      }
      seen.add(k);
    }

    setLoading(true);
    try {
      const requests = validRows.map(row => {
        // The server recomputes the difference from the ledger at save time, so stock that
        // moved after Load is not corrected twice; the batch ends at physicalQty.
        const payload = {
          branchCode: branch,
          itemId: row.selectedItem.itemId,
          batchNo: toServerBatch(row.selectedBatch),
          systemQty: row.systemQty,
          physicalQty: Number(row.actualQty),
          differenceQty: row.difference,
        };
        return axios.post(`/api/${tenancyId}/inventory/stock-correction`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
      });
      await Promise.all(requests);
      setMessage({ text: t("stockCorrectionSuccess"), severity: "success" });
      setRows([emptyRow()]);
    } catch (err) {
      setMessage({ text: t("stockCorrectionError"), severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ flexGrow: 1, p: 3, ml: { xs: 0, sm: "240px" }, mt: 2 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h5" gutterBottom>{t("Physical Stock Correction")}</Typography>
        {message.text && <Alert severity={message.severity} sx={{ mb: 2 }} onClose={() => setMessage({ text: "", severity: "info" })}>{message.text}</Alert>}
        
        <FormControl sx={{ minWidth: 200, mb: 3 }}>
          <InputLabel>{t("Branch")}</InputLabel>
          <Select value={branch} onChange={(e) => setBranch(e.target.value)} label={t("Branch")}>
            {branches.map((b) => <MenuItem key={b.branchCode} value={b.branchCode}>{b.branchCode}</MenuItem>)}
          </Select>
        </FormControl>

        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width="30%">{t("Item Name")}</TableCell>
                <TableCell width="8%" align="center">{t("Load")}</TableCell>
                <TableCell width="18%">{t("Batch")}</TableCell>
                <TableCell align="right">{t("System Qty")}</TableCell>
                <TableCell align="right">{t("Actual Qty")}</TableCell>
                <TableCell align="right">{t("Difference")}</TableCell>
                <TableCell align="center">{t("Action")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>
                    <ItemSearchField
                      value={row.selectedItem}
                      onChange={(v) => handleItemChange(row.id, v)}
                      tenancyId={tenancyId}
                      token={token}
                      t={t}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => handleLoadBatches(row.id)}
                      disabled={!row.selectedItem || !branch || row.loadingBatch}
                      sx={{ minWidth: 60, fontSize: "0.72rem", px: 1 }}
                    >
                      {row.loadingBatch ? <CircularProgress size={14} color="inherit" /> : t("Load")}
                    </Button>
                  </TableCell>
                  <TableCell>
                    <Select fullWidth size="small" value={row.selectedBatch} onChange={(e) => handleBatchChange(row.id, e.target.value)} disabled={row.batches.length === 0}>
                      <MenuItem value="" disabled><em>{t("Click Load first")}</em></MenuItem>
                      {row.batches.map((b) => (
                        <MenuItem key={b.batchNo} value={b.batchNo}>
                          {b.batchNo === "NO STOCK"
                            ? t("No stock in system (qty 0)")
                            : `${b.batchNo === NO_BATCH ? t("(no batch)") : b.batchNo} (${b.currentStock})`}
                        </MenuItem>
                      ))}
                    </Select>
                    {row.batches.length > 1 && (
                      // Item Movement's closing balance is this total, not any single batch.
                      <Typography variant="caption" color="text.secondary">
                        {t("Item total")}: {row.batches.reduce((s, b) => s + b.currentStock, 0).toFixed(3)}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">{row.systemQty.toFixed(3)}</TableCell>
                  <TableCell align="right">
                    {(() => {
                      // Say it while the cursor is still in the field. The database
                      // constraint would catch a barcode too, but only on save and only as
                      // a raw violation nobody can act on.
                      const q = row.actualQty === "" ? null : checkQuantity(row.actualQty);
                      return (
                        <TextField
                          type="number"
                          size="small"
                          value={row.actualQty}
                          onChange={(e) => handleActualQtyChange(row.id, e.target.value)}
                          error={Boolean(q && !q.ok)}
                          helperText={q ? q.error || q.warning || "" : ""}
                          sx={{ width: q && (q.error || q.warning) ? 220 : 100 }}
                        />
                      );
                    })()}
                  </TableCell>
                  <TableCell align="right">{row.difference.toFixed(3)}</TableCell>
                  <TableCell align="center">
                    <IconButton color="error" onClick={() => handleRemoveRow(row.id)} disabled={rows.length === 1}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ mt: 2, display: "flex", justifyContent: "space-between" }}>
          <Stack direction="row" spacing={2}>
            <Button startIcon={<AddIcon />} variant="outlined" onClick={handleAddRow}>
              {t("Add Row")}
            </Button>
          </Stack>
          <Button variant="contained" color="primary" onClick={handleSave} disabled={loading}>
            {loading ? <CircularProgress size={24} /> : t("Save All Corrections")}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default PhysicalStockCorrection;